import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { BillingMilestone } from '../types/agreement'
import {
  BUILTIN_MILESTONE_PRESETS,
  BillingMilestoneTemplateOption,
  MilestoneTemplateRow,
  milestoneRowsForSave,
  sumTemplatePercentages,
} from '../lib/billingMilestoneTemplates'

interface UseBillingMilestoneTemplatesResult {
  /** Built-in + active saved templates for the Apply dropdown. */
  templates: BillingMilestoneTemplateOption[]
  savedTemplates: BillingMilestoneTemplateOption[]
  loading: boolean
  refresh: () => Promise<void>
  saveScheduleAsTemplate: (
    milestones: BillingMilestone[],
    name: string,
    description?: string
  ) => Promise<{ ok: boolean; error?: string }>
}

function mapDbRow(row: any): BillingMilestoneTemplateOption {
  const milestones = Array.isArray(row.milestones) ? (row.milestones as MilestoneTemplateRow[]) : []
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    milestones,
    source: 'saved',
    is_system: !!row.is_system,
  }
}

export function useBillingMilestoneTemplates(): UseBillingMilestoneTemplatesResult {
  const [savedTemplates, setSavedTemplates] = useState<BillingMilestoneTemplateOption[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('billing_milestone_templates')
        .select('id, name, description, milestones, is_active, is_system')
        .eq('is_active', true)
        .order('name', { ascending: true })
      if (error) throw error
      setSavedTemplates((data || []).map(mapDbRow))
    } catch (e) {
      console.warn('Failed to load billing milestone templates (migration may be pending):', e)
      setSavedTemplates([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const templates = useMemo(() => {
    // Prefer code builtins; skip DB system rows that duplicate builtin names.
    const builtinNames = new Set(BUILTIN_MILESTONE_PRESETS.map((p) => p.name.toLowerCase()))
    const savedOnly = savedTemplates.filter(
      (t) => !t.is_system || !builtinNames.has(t.name.toLowerCase())
    )
    return [...BUILTIN_MILESTONE_PRESETS, ...savedOnly]
  }, [savedTemplates])

  const saveScheduleAsTemplate = useCallback(
    async (milestones: BillingMilestone[], name: string, description?: string) => {
      const trimmed = name.trim()
      if (!trimmed) return { ok: false, error: 'Template name is required.' }

      const rows = milestoneRowsForSave(milestones)
      if (rows.length === 0) {
        return { ok: false, error: 'Add labeled milestones before saving a template.' }
      }
      const totalPct = sumTemplatePercentages(rows)
      if (Math.abs(totalPct - 100) > 0.01) {
        return {
          ok: false,
          error: `Milestone percentages must total 100% (currently ${totalPct.toFixed(1)}%).`,
        }
      }

      try {
        const { error } = await supabase.from('billing_milestone_templates').insert({
          name: trimmed,
          description: description?.trim() || null,
          milestones: rows,
          is_active: true,
          is_system: false,
        })
        if (error) throw error
        await refresh()
        return { ok: true }
      } catch (e: any) {
        console.error('Failed to save billing milestone template:', e)
        return { ok: false, error: e?.message || 'Failed to save template.' }
      }
    },
    [refresh]
  )

  return { templates, savedTemplates, loading, refresh, saveScheduleAsTemplate }
}
